import React, {useState, useMemo} from 'react'
import {withInstallation} from '../../components/InstallationContext';
import {useQuery} from "@apollo/client";
import gql from 'graphql-tag'
import {Loading} from "../../components/LoadingFork";
import {DateTime} from "luxon";
import {SelectInput} from "../../components/SelectInput";
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import {Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import ReactApexChart from 'react-apexcharts';
import LazyWidget from '../DashboardV2/LazyWidget';

const useStyles = makeStyles(() => ({
    root: {
        padding: '20px 24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
    },
    title: {
        fontSize: 26,
        fontWeight: 700,
        color: '#1a1a1a',
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: '#1a1a1a',
        marginBottom: 16,
        marginTop: 28,
    },
    card: {
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow',
    },
    statCard: {
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        padding: '20px 24px',
        textAlign: 'center',
    },
    statLabel: {
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        opacity: 0.6,
        marginBottom: 8,
    },
    statValue: {
        fontSize: 20,
        fontWeight: 700,
    },
}));

export const fmtCurrency = (val) => `$${(val || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

export function buildYearOptions(currentYear, createdAt) {
    const createdYear = createdAt
        ? DateTime.fromISO(createdAt).year
        : currentYear;
    const years = [];
    for (let y = currentYear; y >= createdYear; y--) {
        years.push({value: y, label: String(y)});
    }
    return years;
}

export function groupRateCardsWithChargebacks(rawRateCards, totalBookings, totalChargebackCount) {
    if (totalBookings === 0 && totalChargebackCount === 0) return rawRateCards;
    const threshold = (totalBookings + totalChargebackCount) * 0.03;
    const major = [];
    let othersCount = 0;
    for (const rc of rawRateCards) {
        if (rc.count >= threshold) {
            major.push(rc);
        } else {
            othersCount += rc.count;
        }
    }
    if (othersCount > 0) {
        major.push({name: 'Others', count: othersCount});
    }
    if (totalChargebackCount > 0) {
        major.push({name: 'Chargeback Bookings', count: totalChargebackCount});
    }
    return major;
}

const GET_MONTHLY_REVENUE = gql`
    query GetMonthlyRevenue($installationId: ID!, $year: String!) {
        getMonthlyRevenue(installationId: $installationId, year: $year) {
            month
            monthName
            revenue
            bookings
            prepaid
            payAtGateRevenue
            chargebackCount
        }
    }
`;

const GET_YEARLY_RATE_CARD_SPLIT = gql`
    query GetYearlyRateCardSplit($installationId: ID!, $year: String!) {
        getYearlyRateCardSplit(installationId: $installationId, year: $year) {
            name
            count
        }
    }
`;

function MonthSummary({installation}) {
    const classes = useStyles();
    const timezone = installation.timeZoneName;
    const currentYear = DateTime.now().setZone(timezone).year;

    const [selectedYear, setSelectedYear] = useState(currentYear);

    const yearOptions = useMemo(
        () => buildYearOptions(currentYear, installation.createdAt),
        [currentYear, installation.createdAt]
    );

    const queryVars = {
        variables: {
            installationId: installation.id,
            year: String(selectedYear),
        },
        fetchPolicy: "no-cache",
    };

    const {data, loading} = useQuery(GET_MONTHLY_REVENUE, queryVars);
    const {data: rateCardData, loading: rateCardLoading} = useQuery(GET_YEARLY_RATE_CARD_SPLIT, queryVars);

    const rows = data?.getMonthlyRevenue || [];
    const totalRevenue = rows.reduce((sum, r) => sum + r.revenue, 0);
    const totalDrivoRevenue = rows.reduce((sum, r) => sum + r.prepaid, 0);
    const totalPayAtGate = rows.reduce((sum, r) => sum + (r.payAtGateRevenue || 0), 0);
    const totalChargebackCount = rows.reduce((sum, r) => sum + (r.chargebackCount || 0), 0);

    const rawRateCards = rateCardData?.getYearlyRateCardSplit || [];
    const totalBookings = rawRateCards.reduce((sum, r) => sum + r.count, 0);

    const rateCards = useMemo(() => {
        return groupRateCardsWithChargebacks(rawRateCards, totalBookings, totalChargebackCount);
    }, [rawRateCards, totalBookings, totalChargebackCount]);

    const chartOptions = useMemo(() => ({
        chart: {
            type: 'bar',
            toolbar: {show: true, tools: {download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false}},
            fontFamily: 'inherit',
        },
        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 4,
            },
        },
        dataLabels: {enabled: false},
        xaxis: {
            categories: rows.map(r => r.monthName.substring(0, 3)),
        },
        yaxis: {
            title: {text: 'Revenue ($)'},
            labels: {
                formatter: (val) => `$${val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val.toFixed(0)}`,
            },
        },
        colors: ['#2196F3', '#4CAF50'],
        legend: {
            position: 'top',
            horizontalAlign: 'center',
            fontSize: '14px',
            fontWeight: 500,
        },
        tooltip: {
            y: {
                formatter: (val) => fmtCurrency(val),
            },
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent'],
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    }), [rows]);

    const chartSeries = useMemo(() => [
        {
            name: 'DRIVO Bookings',
            type: 'bar',
            data: rows.map(r => parseFloat(r.prepaid.toFixed(2))),
        },
        {
            name: 'Pay-At-Gate',
            type: 'bar',
            data: rows.map(r => parseFloat((r.payAtGateRevenue || 0).toFixed(2))),
        },
    ], [rows]);

    const donutOptions = useMemo(() => ({
        chart: {
            type: 'donut',
            fontFamily: 'inherit',
        },
        labels: rateCards.map(r => r.name),
        legend: {
            position: 'right',
            fontSize: '13px',
            fontWeight: 500,
            width: 280,
            formatter: (seriesName, opts) => {
                const count = opts.w.globals.series[opts.seriesIndex];
                return `${seriesName} — ${count}`;
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => val >= 5 ? `${val.toFixed(1)}%` : '',
            dropShadow: {enabled: false},
            style: {
                fontSize: '12px',
                fontWeight: 600,
            },
        },
        tooltip: {
            y: {
                formatter: (val) => `${val} bookings`,
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total Bookings',
                            fontSize: '14px',
                            fontWeight: 600,
                            formatter: () => (totalBookings + totalChargebackCount).toLocaleString(),
                        },
                    },
                },
            },
        },
    }), [rateCards, totalBookings, totalChargebackCount]);

    const donutSeries = useMemo(() => rateCards.map(r => r.count), [rateCards]);

    const statCards = [
        {label: 'Total Revenue', value: fmtCurrency(totalRevenue), color: '#3127B8'},
        {label: 'DRIVO Bookings', value: fmtCurrency(totalDrivoRevenue), color: '#2196F3'},
        {label: 'Pay-At-Gate Revenue', value: fmtCurrency(totalPayAtGate), color: '#4CAF50'},
        {label: 'Chargeback Bookings', value: totalChargebackCount, color: '#FF9800'},
    ];

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span className={classes.title}>
                    Monthly Summary — {selectedYear}
                </span>
                <div style={{width: 160}}>
                    <SelectInput
                        label="Year"
                        options={yearOptions}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    />
                </div>
            </div>

            {loading && <Loading/>}

            {!loading && rows.length === 0 && (
                <p style={{fontSize: 16, textAlign: 'center', marginTop: 40, color: '#999'}}>
                    No revenue data available for {selectedYear}.
                </p>
            )}

            {!loading && rows.length > 0 && (
                <>
                    <Grid container spacing={3} style={{marginBottom: 24}}>
                        {statCards.map((stat) => (
                            <Grid item xs={12} sm={6} md={3} key={stat.label}>
                                <div className={classes.statCard}>
                                    <div className={classes.statLabel}>{stat.label}</div>
                                    <div className={classes.statValue} style={{color: stat.color}}>
                                        {stat.value}
                                    </div>
                                </div>
                            </Grid>
                        ))}
                    </Grid>

                    <Card className={classes.card}>
                        <CardContent>
                            <ReactApexChart
                                options={chartOptions}
                                series={chartSeries}
                                type="bar"
                                height={400}
                            />
                        </CardContent>
                    </Card>

                    <LazyWidget minHeight={450}>
                        {(isVisible) => isVisible && !rateCardLoading && rateCards.length > 0 && (
                            <>
                                <div className={classes.sectionTitle}>Bookings by Guest Card — {selectedYear}</div>
                                <Card className={classes.card}>
                                    <CardContent>
                                        <ReactApexChart
                                            options={donutOptions}
                                            series={donutSeries}
                                            type="donut"
                                            height={400}
                                        />
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </LazyWidget>
                </>
            )}
        </div>
    );
}

export default withInstallation(MonthSummary)
